import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";

import {
  useApproveSplit,
  useFacets,
  useJob,
  useJobs,
  useMarkSplitExempt,
  useRejectSplit,
  useSplitDistribution,
  useSplitInFlight,
  useSplitPreview,
  useSplitProposals,
  useStartSplitJob,
  useSubmitJob,
  type SplitPreviewParams,
  type SplitProposalOut,
} from "@/api/queries";
import { useCan, useMember } from "@/auth/member";
import { SplitStepper } from "@/components/pipeline-stepper";
import { SpeechChip } from "@/components/speech-chip";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Combobox, FieldLabel, Input, Select, Textarea } from "@/components/ui/input";
import { Callout, Empty, ErrorNote, Panel, PanelHeader } from "@/components/ui/panel";
import { shortName, whoLabel } from "@/lib/people";
import { cn, formatDate } from "@/lib/utils";

/**
 * The Splits tab — DESIGN §17.11.5-6. Two things on one screen because they are one workflow: ask the
 * worker to look at some of the bank (a run sized by "how many"), then decide what it proposes.
 *
 * Nothing here changes a live question until a proposal is approved. A run only writes proposals, so a
 * poor one costs quota and nothing else — which is why Reject keeps the row (a rejected question is not
 * offered again) and why there is no delete.
 */
export function SplitReview({ onOpenQueue }: { onOpenQueue: () => void }) {
  // Several people can run batches against the same bank at once, so the tracker and the proposals
  // default to the runs *you* started; "Everyone" is one click away (DESIGN §17.11.7).
  const [scope, setScope] = useState<"mine" | "everyone">("mine");
  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-auto p-4 [&>*]:shrink-0">
      <RunPanel />
      <ScopeToggle scope={scope} onChange={setScope} />
      <InFlightPanel key={`flight-${scope}`} mine={scope === "mine"} onOpenQueue={onOpenQueue} />
      <Queue key={`queue-${scope}`} scope={scope} onScope={setScope} />
    </div>
  );
}

type Scope = "mine" | "everyone";

/** Whose runs the tracker and the proposals show. Offered both above the tracker and in the Proposals
 * header, bound to one state, because a reviewer looking at a proposal is not looking at the top of the
 * page (DESIGN 17.11.7). */
function ScopeToggle({ scope, onChange }: { scope: Scope; onChange: (scope: Scope) => void }) {
  return (
    <div role="tablist" aria-label="Whose runs" className="flex items-center gap-1">
      <span className="text-bank-muted mr-1 text-xs">Show</span>
      {(
        [
          ["mine", "My runs"],
          ["everyone", "Everyone's runs"],
        ] as const
      ).map(([value, label]) => (
        <button
          key={value}
          role="tab"
          aria-selected={scope === value}
          onClick={() => onChange(value)}
          className={cn(
            "focus-visible:ring-bank-accent rounded-md px-2 py-1 font-mono text-[11px] focus-visible:ring-2 focus-visible:outline-none",
            scope === value
              ? "bg-bank-accent-wash text-bank-accent-ink"
              : "text-bank-muted hover:bg-bank-surface-2",
          )}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------- after approval: the pipeline, live

/**
 * What happens after Approve. The rewritten question and its new follow-up go back through the
 * pipeline — tagging, fingerprinting, the duplicate check — which takes the worker a minute or so,
 * and until now nothing on this screen said so. This is the ingest run page's stepper, for those
 * questions, plus a line on the pipeline run itself: waiting, running, or stalled.
 */
/** " (queued by Bhavin Surelia)" — whose pipeline run a status line is describing, now that more than
 * one person can have one going. */
function byLine(queuedBy: string | null | undefined): string {
  return queuedBy ? ` (queued by ${shortName(queuedBy)})` : "";
}

function InFlightPanel({ onOpenQueue, mine }: { onOpenQueue: () => void; mine: boolean }) {
  const [picked, setPicked] = useState<number | undefined>(undefined);
  const { data } = useSplitInFlight(picked, mine);
  const canRun = useCan("run_pipeline");
  const runPipeline = useSubmitJob();
  if (!data || data.total === 0) return null;

  const job = data.pipeline_job;
  const waiting = (data.counts.pending_review ?? 0) + (data.counts.stale ?? 0);
  let line: React.ReactNode;
  if (!data.active) {
    line = (
      <>
        All {data.total} are through the automatic steps.
        {waiting > 0 &&
          ` ${waiting} ${waiting === 1 ? "is" : "are"} waiting for a decision in the Review queue.`}
      </>
    );
  } else if (job?.paused_for_quota) {
    line = `Paused — today's allowance for ${job.quota_model ?? "the model"} is used up. It resumes by itself when quota returns.`;
  } else if (job?.paused_for_provider) {
    line = "Paused — the AI service is rate limited or unreachable. It retries by itself every few minutes.";
  } else if (job && job.status === "running" && job.heartbeat_stale) {
    line = `Pipeline run #${job.id}${byLine(job.queued_by)} has stopped reporting — its worker may be down. It is picked up again automatically once a worker is running.`;
  } else if (job && job.status === "running") {
    line = `Pipeline run #${job.id}${byLine(job.queued_by)} is running — these move on every few seconds.`;
  } else if (job && job.status === "queued") {
    line = `Pipeline run #${job.id}${byLine(job.queued_by)} is queued — waiting for the worker to pick it up.`;
  } else {
    line = "No pipeline run is waiting to carry these on, so they will sit here until one is started.";
  }
  const stalled = data.active && !(job && (job.status === "running" || job.status === "queued"));
  const current = data.batches.find((b) => b.job_id === data.job_id);

  return (
    <Panel>
      <PanelHeader
        title={
          (data.active ? "Approved — going through the pipeline" : "Approved — through the pipeline") +
          (data.job_id ? ` · batch run #${data.job_id}` : "") +
          (current ? ` · started by ${whoLabel(current.started_by, current.is_mine)}` : "") +
          (current?.stack ? ` · ${current.stack}` : "")
        }
        actions={
          waiting > 0 ? (
            <Button size="sm" onClick={onOpenQueue}>
              Open the review queue
            </Button>
          ) : undefined
        }
      />
      <div className="space-y-3 p-4">
        {data.batches.length > 1 && (
          <div role="tablist" aria-label="Batch" className="flex flex-wrap items-center gap-1">
            <span className="text-bank-muted mr-1 text-xs">Batch</span>
            {data.batches.map((b) => (
              <button
                key={b.job_id}
                role="tab"
                aria-selected={b.job_id === data.job_id}
                onClick={() => setPicked(b.job_id)}
                className={cn(
                  "focus-visible:ring-bank-accent rounded-md px-2 py-1 font-mono text-[11px] focus-visible:ring-2 focus-visible:outline-none",
                  b.job_id === data.job_id
                    ? "bg-bank-accent-wash text-bank-accent-ink"
                    : "text-bank-muted hover:bg-bank-surface-2",
                )}
              >
                run #{b.job_id} · {whoLabel(b.started_by, b.is_mine)}
                {b.stack ? ` · ${b.stack}` : ""} <span className="tabular-nums">{b.total}</span>
                {b.active && " …"}
              </button>
            ))}
          </div>
        )}
        <SplitStepper questions={data.counts} total={data.total} />
        <Callout
          tone={stalled ? "bad" : data.active ? "attention" : "good"}
          className="flex flex-wrap items-center gap-2"
        >
          <span>{line}</span>
          {stalled && canRun && (
            <Button
              size="sm"
              variant="primary"
              disabled={runPipeline.isPending}
              onClick={() => runPipeline.mutate("pipeline")}
            >
              {runPipeline.isPending ? "Queuing…" : "Run pipeline now"}
            </Button>
          )}
        </Callout>
        <ErrorNote error={runPipeline.error} />
        <details className="text-sm">
          <summary className="text-bank-muted cursor-pointer font-mono text-[11px]">
            the {data.total} questions
          </summary>
          <ul className="mt-2 space-y-1.5">
            {data.items.map((item) => (
              <li key={item.question_id} className="flex items-start gap-2">
                <span className="text-bank-muted shrink-0 font-mono text-[11px] tabular-nums">
                  #{item.question_id}
                </span>
                <Badge value={item.status} />
                <span className="text-bank-muted shrink-0 font-mono text-[10.5px] uppercase">
                  {item.role === "primary" ? "primary" : "follow-up"}
                </span>
                <span className="question-text min-w-0 flex-1 text-[13px]">{item.text}</span>
              </li>
            ))}
          </ul>
        </details>
      </div>
    </Panel>
  );
}

// ---------------------------------------------------------------- the batch run

function RunPanel() {
  const canRun = useCan("ingest");
  const { data: facets } = useFacets();
  const [limit, setLimit] = useState("25");
  const [order, setOrder] = useState<"longest_first" | "id">("longest_first");
  const [stack, setStack] = useState("");
  const [minSeconds, setMinSeconds] = useState("");
  const [asked, setAsked] = useState<SplitPreviewParams | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [queued, setQueued] = useState<number | null>(null);

  const params = useMemo<SplitPreviewParams>(() => {
    const n = Math.min(Math.max(Math.trunc(Number(limit)) || 0, 1), 500);
    const seconds = Number(minSeconds);
    return {
      limit: n,
      order,
      ...(stack ? { stack } : {}),
      ...(seconds > 0 ? { min_seconds: seconds } : {}),
    };
  }, [limit, order, stack, minSeconds]);

  const preview = useSplitPreview(asked ?? params, asked !== null);
  const start = useStartSplitJob();

  function doPreview() {
    setConfirming(false);
    if (asked && JSON.stringify(asked) === JSON.stringify(params)) void preview.refetch();
    else setAsked(params);
  }

  function doRun() {
    if (!confirming) {
      setConfirming(true);
      return;
    }
    start.mutate(
      { limit: params.limit, order, stack: stack || null, min_seconds: params.min_seconds ?? null },
      {
        onSuccess: (job) => {
          setQueued(job.job_id);
          setConfirming(false);
        },
      },
    );
  }

  const shown = preview.data;
  return (
    <Panel>
      <PanelHeader title="Split long questions" />
      <div className="space-y-3 p-4">
        <p className="text-bank-muted max-w-[78ch] text-sm">
          Asks the model about approved questions that take too long to say aloud, and proposes a shorter
          primary and, where there are two asks, a follow-up that stands on its own. Nothing changes until you
          approve a proposal below.
        </p>

        <div className="grid gap-3 sm:grid-cols-[1fr_1fr_2fr_1fr]">
          <div>
            <FieldLabel htmlFor="split-limit">How many</FieldLabel>
            <Input
              id="split-limit"
              type="number"
              min={1}
              max={500}
              value={limit}
              onChange={(e) => {
                setLimit(e.target.value);
                setConfirming(false);
              }}
            />
          </div>
          <div>
            <FieldLabel htmlFor="split-order">Order</FieldLabel>
            <Select
              id="split-order"
              value={order}
              onChange={(e) => {
                setOrder(e.target.value as "longest_first" | "id");
                setConfirming(false);
              }}
            >
              <option value="longest_first">longest first</option>
              <option value="id">by id</option>
            </Select>
          </div>
          <div>
            <FieldLabel htmlFor="split-stack">Stack</FieldLabel>
            <Combobox
              id="split-stack"
              value={stack}
              onChange={(v) => {
                setStack(v);
                setConfirming(false);
              }}
              options={[
                { value: "", label: "any" },
                ...(facets?.stack_paths ?? []).map((s) => ({ value: s, label: s })),
              ]}
            />
          </div>
          <div>
            <FieldLabel htmlFor="split-min">Only over (s)</FieldLabel>
            <Input
              id="split-min"
              type="number"
              min={1}
              step={0.5}
              placeholder="default"
              value={minSeconds}
              onChange={(e) => {
                setMinSeconds(e.target.value);
                setConfirming(false);
              }}
            />
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" onClick={doPreview} disabled={preview.isFetching}>
            {preview.isFetching ? "Counting…" : "Preview (no model call)"}
          </Button>
          {canRun && (
            <Button
              size="sm"
              variant={confirming ? "primary" : "secondary"}
              disabled={start.isPending || (shown !== undefined && shown.would_send === 0)}
              onClick={doRun}
            >
              {start.isPending
                ? "Queuing…"
                : confirming
                  ? `Confirm: look at up to ${params.limit} question${params.limit === 1 ? "" : "s"}`
                  : "Run"}
            </Button>
          )}
          {confirming && (
            <Button size="sm" variant="ghost" onClick={() => setConfirming(false)}>
              Cancel
            </Button>
          )}
          {shown && (
            <span className="text-bank-muted font-mono text-xs tabular-nums">
              {shown.eligible} eligible · a run of {params.limit} sends {shown.would_send} in about{" "}
              {shown.model_calls} request{shown.model_calls === 1 ? "" : "s"}
            </span>
          )}
        </div>

        <ErrorNote error={preview.error ?? start.error} />
        {queued !== null && <JobProgress jobId={queued} onDismiss={() => setQueued(null)} />}
        <RecentRuns />
        {shown && shown.sample.length > 0 && (
          <details className="text-sm">
            <summary className="text-bank-muted cursor-pointer font-mono text-[11px]">
              the {shown.sample.length} it would start with
            </summary>
            <ul className="mt-2 space-y-1.5">
              {shown.sample.map((s) => (
                <li key={s.id} className="flex items-start gap-2">
                  <span className="text-bank-muted shrink-0 font-mono text-[11px] tabular-nums">#{s.id}</span>
                  <span className="question-text min-w-0 flex-1 text-[13px]">{s.text}</span>
                  <span className="text-bank-muted shrink-0 font-mono text-[11px] tabular-nums">
                    ~{s.seconds.toFixed(1)}s
                  </span>
                </li>
              ))}
            </ul>
          </details>
        )}
      </div>
    </Panel>
  );
}

// ---------------------------------------------------------------- progress and past runs

type SplitTotals = { eligible?: number; examined?: number; proposed?: number; kept?: number; calls?: number };

/**
 * What a run is doing right now. The worker reports after each model call (a batch of
 * `split.batch_size` questions), so the count moves in steps, and for the first call there is
 * nothing to show but "asking" — saying so beats a bar that sits at zero looking stuck.
 */
function JobProgress({ jobId, onDismiss }: { jobId: number; onDismiss: () => void }) {
  const job = useJob(jobId);
  const client = useQueryClient();
  const status = job.data?.status ?? "queued";
  const finished = status === "done" || status === "failed" || status === "cancelled";

  // The proposals list is polled, but a finished run should show its proposals at once.
  useEffect(() => {
    if (finished) void client.invalidateQueries({ queryKey: ["splits"] });
  }, [finished, client]);

  const totals = (job.data?.totals ?? {}) as SplitTotals;
  const cursor = (job.data?.cursor ?? {}) as { paused_reason?: string; quota_model?: string };
  const total = totals.eligible ?? 0;
  const examined = totals.examined ?? 0;
  const percent = total > 0 ? Math.min(100, Math.round((100 * examined) / total)) : 0;
  const tone = status === "failed" ? "bad" : status === "done" ? "good" : "attention";

  let message: string;
  if (status === "failed") message = job.data?.error ?? "The run failed.";
  else if (status === "cancelled") message = "The run was cancelled before it started.";
  else if (status === "done")
    message = `Done — looked at ${examined}: ${totals.proposed ?? 0} proposed, ${totals.kept ?? 0} left as written. The proposals are below.`;
  else if (cursor.paused_reason === "quota")
    message = `Paused — today's allowance for ${cursor.quota_model ?? "the model"} is used up. It resumes by itself when quota returns.`;
  else if (status === "queued") message = "Waiting for the worker to pick it up…";
  else if (examined === 0)
    message = `Asking the model about the first ${Math.min(total || 20, 20)} questions — this takes a little while…`;
  else message = `Looked at ${examined} of ${total} · ${totals.proposed ?? 0} proposed so far`;

  return (
    <Callout tone={tone} className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-mono text-xs">Job #{jobId}</span>
        <Badge value={status} />
        <span>{message}</span>
        <Button size="sm" variant="ghost" className="ml-auto" onClick={onDismiss}>
          Dismiss
        </Button>
      </div>
      {!finished && (
        <div
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={total || 100}
          aria-valuenow={examined}
          className="bg-bank-surface-3 h-1.5 overflow-hidden rounded"
        >
          <div className="bg-bank-accent h-full transition-[width]" style={{ width: `${percent}%` }} />
        </div>
      )}
    </Callout>
  );
}

/** The last few split runs, here as well as in the Ingest page's Jobs table, because this is where
 * a person who started one looks for it. */
function RecentRuns() {
  const me = useMember();
  const jobs = useJobs(5, "split");
  const items = jobs.data?.items ?? [];
  if (items.length === 0) return null;
  return (
    <details className="text-sm" open>
      <summary className="text-bank-muted cursor-pointer font-mono text-[11px]">recent runs</summary>
      <ul className="mt-2 space-y-1">
        {items.map((job) => {
          const t = (job.totals ?? {}) as SplitTotals;
          const by = typeof job.params?.queued_by === "string" ? job.params.queued_by : undefined;
          const stack = typeof job.params?.stack === "string" ? job.params.stack : undefined;
          return (
            <li key={job.id} className="flex flex-wrap items-center gap-2">
              <span className="text-bank-muted font-mono text-[11px] tabular-nums">#{job.id}</span>
              <Badge value={job.status} />
              <span className="text-bank-muted font-mono text-[11px]">
                {whoLabel(by, !!by && by.includes(`(${me.member_no})`))}
                {stack ? ` · ${stack}` : ""}
              </span>
              <span className="text-bank-muted font-mono text-[11px]">{formatDate(job.started_at)}</span>
              <span className="text-bank-muted font-mono text-[11px]">
                {t.examined === undefined
                  ? "—"
                  : `${t.examined}${t.eligible ? ` of ${t.eligible}` : ""} looked at · ${t.proposed ?? 0} proposed · ${t.kept ?? 0} kept`}
              </span>
              {job.error && <span className="text-bank-bad-ink text-xs">{job.error.slice(0, 120)}</span>}
            </li>
          );
        })}
      </ul>
    </details>
  );
}

// ---------------------------------------------------------------- the proposals

const PAGE = 20;
const TABS = [
  { value: "proposed", label: "Waiting" },
  { value: "applied", label: "Applied" },
  { value: "rejected", label: "Rejected" },
  { value: "exempt", label: "Long on purpose" },
] as const;

type Edit = { primary?: string; followUp?: string };

function Queue({ scope, onScope }: { scope: Scope; onScope: (scope: Scope) => void }) {
  const mine = scope === "mine";
  const canReview = useCan("review");
  const [tab, setTab] = useState<(typeof TABS)[number]["value"]>("proposed");
  const [offset, setOffset] = useState(0);
  const list = useSplitProposals(tab, offset, PAGE, mine);
  const { data: distribution } = useSplitDistribution();
  const limit = distribution?.limit_seconds ?? 6;
  const [edits, setEdits] = useState<Record<number, Edit>>({});
  const [ticked, setTicked] = useState<Set<number>>(new Set());
  const [bulkError, setBulkError] = useState<string | null>(null);
  const [approved, setApproved] = useState<{ count: number; pipelineJob: number | null } | null>(null);
  const [bulkRunning, setBulkRunning] = useState(false);

  const approve = useApproveSplit();
  const reject = useRejectSplit();
  const exempt = useMarkSplitExempt();
  const items = list.data?.items ?? [];
  const counts = list.data?.counts ?? {};
  const busy = approve.isPending || reject.isPending || exempt.isPending || bulkRunning;
  const clean = items.filter((p) => p.findings.length === 0);

  function toggle(id: number) {
    setTicked((cur) => {
      const next = new Set(cur);
      if (!next.delete(id)) next.add(id);
      return next;
    });
  }

  function patch(id: number, change: Edit) {
    setEdits((cur) => ({ ...cur, [id]: { ...cur[id], ...change } }));
  }

  function approveOne(p: SplitProposalOut) {
    const e = edits[p.id];
    approve.mutate(
      { id: p.id, primaryText: e?.primary, followUpText: e?.followUp },
      { onSuccess: (done) => setApproved({ count: 1, pipelineJob: done.pipeline_job_id ?? null }) },
    );
  }

  async function bulk(kind: "approve" | "reject") {
    setBulkRunning(true);
    setBulkError(null);
    try {
      let pipelineJob: number | null = null;
      // Findings are advisory (DESIGN §17.11.6): a person who ticked a flagged card has chosen it,
      // so bulk approval takes everything ticked and the toolbar says how many were flagged.
      const ids = [...ticked];
      for (const id of ids) {
        if (kind === "approve") {
          const done = await approve.mutateAsync({ id });
          pipelineJob = pipelineJob ?? done.pipeline_job_id ?? null;
        } else await reject.mutateAsync(id);
      }
      if (kind === "approve") setApproved({ count: ids.length, pipelineJob });
      setTicked(new Set());
    } catch (e) {
      setBulkError(e instanceof Error ? e.message : String(e));
    } finally {
      setBulkRunning(false);
    }
  }

  // Findings are advisory, so they do not block a bulk approve; the toolbar just says how many of
  // the ticked ones carry one (DESIGN §17.11.6).
  const tickedWithFindings = items.filter((p) => ticked.has(p.id) && p.findings.length > 0).length;
  const allTicked = items.length > 0 && items.every((p) => ticked.has(p.id));
  const someTicked = items.some((p) => ticked.has(p.id));
  const selectAllRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (selectAllRef.current) selectAllRef.current.indeterminate = someTicked && !allTicked;
  }, [someTicked, allTicked]);

  return (
    <Panel className="flex flex-col">
      <PanelHeader
        title="Proposals"
        actions={
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
            <ScopeToggle scope={scope} onChange={onScope} />
            <div role="tablist" aria-label="Proposal status" className="flex gap-1">
              {TABS.map((t) => (
                <button
                  key={t.value}
                  role="tab"
                  aria-selected={tab === t.value}
                  onClick={() => {
                    setTab(t.value);
                    setOffset(0);
                    setTicked(new Set());
                  }}
                  className={cn(
                    "focus-visible:ring-bank-accent rounded-md px-2 py-1 font-mono text-[11px] focus-visible:ring-2 focus-visible:outline-none",
                    tab === t.value
                      ? "bg-bank-accent-wash text-bank-accent-ink"
                      : "text-bank-muted hover:bg-bank-surface-2",
                  )}
                >
                  {t.label} <span className="tabular-nums">{counts[t.value] ?? 0}</span>
                </button>
              ))}
            </div>
          </div>
        }
      />

      {tab === "proposed" && canReview && items.length > 0 && (
        <div className="border-bank-border flex flex-wrap items-center gap-2 border-b px-4 py-2">
          <label className="flex items-center gap-1.5 text-xs font-medium">
            <input
              ref={selectAllRef}
              type="checkbox"
              checked={allTicked}
              onChange={() => setTicked(allTicked ? new Set() : new Set(items.map((p) => p.id)))}
              className="accent-bank-accent size-3.5"
            />
            Select all {items.length} on this page
          </label>
          <Button size="sm" onClick={() => setTicked(new Set(clean.map((p) => p.id)))}>
            Tick the {clean.length} with no findings
          </Button>
          <Button size="sm" variant="ghost" disabled={ticked.size === 0} onClick={() => setTicked(new Set())}>
            Clear
          </Button>
          <Button
            size="sm"
            variant="primary"
            disabled={busy || ticked.size === 0}
            onClick={() => void bulk("approve")}
          >
            Approve {ticked.size || ""} ticked
          </Button>
          <Button
            size="sm"
            variant="danger"
            disabled={busy || ticked.size === 0}
            onClick={() => void bulk("reject")}
          >
            Reject {ticked.size || ""} ticked
          </Button>
          {tickedWithFindings > 0 && (
            <span className="text-bank-attention-ink text-xs">
              {tickedWithFindings} ticked {tickedWithFindings === 1 ? "has" : "have"} findings that you have
              not read individually — Approve will include {tickedWithFindings === 1 ? "it" : "them"}.
            </span>
          )}
          <span className="text-bank-muted text-xs">
            Approving rewrites the question and sends it, and its follow-up, back through the pipeline.{" "}
            <span className="text-bank-attention-ink">
              An orange outline means the checks noticed something — see the note on the card; those are
              approved one at a time.
            </span>
          </span>
        </div>
      )}

      <div className="p-4">
        <ErrorNote error={list.error ?? approve.error ?? reject.error ?? exempt.error ?? bulkError} />
        {approved && (
          <Callout tone="good" className="mb-3 flex flex-wrap items-center gap-2">
            <span>
              Approved {approved.count} proposal{approved.count === 1 ? "" : "s"}. The rewritten question and
              its follow-up now go back through tagging, which writes the rubric and difficulty —{" "}
              {approved.pipelineJob !== null
                ? `a pipeline run (job #${approved.pipelineJob}) is queued and the worker will pick it up.`
                : "a pipeline run is already queued and will cover them."}{" "}
              Until it finishes they show as <span className="font-mono">split_checked</span>, and the
              rewritten question is out of the bank until it is approved again.
            </span>
            <Button size="sm" variant="ghost" className="ml-auto" onClick={() => setApproved(null)}>
              Dismiss
            </Button>
          </Callout>
        )}
        {items.length === 0 && !list.isLoading ? (
          <Empty>
            {tab === "proposed"
              ? mine
                ? "Nothing waiting from your runs. Run a batch above, or switch to Everyone's runs."
                : "Nothing waiting. Run a batch above to get proposals."
              : "Nothing here."}
          </Empty>
        ) : (
          <ul className="space-y-3">
            {items.map((p) => (
              <li
                key={p.id}
                className={cn(
                  "border-bank-border rounded-md border p-3",
                  p.findings.length > 0 && "border-bank-attention",
                )}
              >
                <div className="flex flex-wrap items-center gap-2">
                  {tab === "proposed" && canReview && (
                    <input
                      type="checkbox"
                      checked={ticked.has(p.id)}
                      onChange={() => toggle(p.id)}
                      aria-label={`Tick proposal for question #${p.id}`}
                      className="accent-bank-accent size-3.5"
                    />
                  )}
                  <span className="text-bank-muted font-mono text-[11px] tabular-nums">#{p.question_id}</span>
                  <Badge value={p.status} />
                  <span className="text-bank-muted font-mono text-[11px]">{p.stack_path ?? "—"}</span>
                  <span className="text-bank-muted font-mono text-[11px]">{p.origin}</span>
                  {p.job_id ? (
                    <span
                      className={cn(
                        "rounded px-1.5 py-0.5 font-mono text-[10.5px]",
                        p.is_mine
                          ? "bg-bank-accent-wash text-bank-accent-ink"
                          : "bg-bank-surface-2 text-bank-muted",
                      )}
                      title="the batch run that proposed this, and who started it"
                    >
                      run #{p.job_id} · {whoLabel(p.started_by, p.is_mine)}
                    </span>
                  ) : null}
                  {tab !== "proposed" && p.decided_by && (
                    <span className="text-bank-muted font-mono text-[11px]">
                      {tab === "applied" ? "approved" : tab === "rejected" ? "rejected" : "marked"} by{" "}
                      {shortName(p.decided_by)}
                    </span>
                  )}
                </div>

                <p className="text-bank-muted question-text mt-2 text-[13px]">
                  <span className="mr-1.5 font-mono text-[10.5px] tracking-[0.07em] uppercase">was</span>
                  {p.original_text}
                  <SpeechChip seconds={p.seconds_before} limit={limit} className="ml-2" />
                </p>

                <Part
                  label="primary"
                  value={edits[p.id]?.primary ?? p.proposed_primary_text ?? ""}
                  seconds={edits[p.id]?.primary === undefined ? p.seconds_primary : null}
                  limit={limit}
                  editable={tab === "proposed" && canReview}
                  onChange={(v) => patch(p.id, { primary: v })}
                />
                {(p.proposed_follow_up_text || edits[p.id]?.followUp !== undefined) && (
                  <Part
                    label="follow-up"
                    value={edits[p.id]?.followUp ?? p.proposed_follow_up_text ?? ""}
                    seconds={edits[p.id]?.followUp === undefined ? p.seconds_follow_up : null}
                    limit={limit}
                    editable={tab === "proposed" && canReview}
                    onChange={(v) => patch(p.id, { followUp: v })}
                  />
                )}
                {!p.proposed_follow_up_text && p.proposed_primary_text && (
                  <p className="text-bank-muted mt-1 text-xs">
                    Shortened only — there is no second ask to split off.
                  </p>
                )}

                {p.findings.length > 0 && (
                  <ul className="text-bank-attention-ink mt-2 list-disc space-y-0.5 pl-4 text-xs">
                    {p.findings.map((f, i) => (
                      <li key={i}>{f}</li>
                    ))}
                  </ul>
                )}

                {tab === "proposed" && canReview && (
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <Button size="sm" variant="primary" disabled={busy} onClick={() => approveOne(p)}>
                      Approve
                    </Button>
                    <Button size="sm" variant="danger" disabled={busy} onClick={() => reject.mutate(p.id)}>
                      Reject
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={busy}
                      title="Long on purpose — never offered for splitting again"
                      onClick={() => exempt.mutate(p.question_id)}
                    >
                      Long on purpose
                    </Button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}

        {(list.data?.total ?? 0) > PAGE && (
          <div className="mt-3 flex items-center justify-between">
            <p className="text-bank-muted font-mono text-[11px] tabular-nums">
              {offset + 1}–{Math.min(offset + PAGE, list.data?.total ?? 0)} of {list.data?.total}
            </p>
            <div className="flex gap-1.5">
              <Button size="sm" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE))}>
                Previous
              </Button>
              <Button
                size="sm"
                disabled={offset + PAGE >= (list.data?.total ?? 0)}
                onClick={() => setOffset(offset + PAGE)}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </div>
    </Panel>
  );
}

function Part({
  label,
  value,
  seconds,
  limit,
  editable,
  onChange,
}: {
  label: string;
  value: string;
  seconds: number | null | undefined;
  limit: number;
  editable: boolean;
  onChange: (value: string) => void;
}) {
  return (
    <div className="mt-2 flex items-start gap-2">
      <span className="text-bank-muted mt-1.5 w-16 shrink-0 font-mono text-[10.5px] tracking-[0.07em] uppercase">
        {label}
      </span>
      {editable ? (
        <Textarea
          value={value}
          rows={2}
          onChange={(e) => onChange(e.target.value)}
          aria-label={`${label} question`}
          className="min-h-0 flex-1 text-[13px]"
        />
      ) : (
        <p className="question-text min-w-0 flex-1 text-[13px]">{value}</p>
      )}
      <span className="mt-1 shrink-0">
        {seconds === null || seconds === undefined ? (
          <span className="text-bank-muted font-mono text-[10.5px]">edited</span>
        ) : (
          <SpeechChip seconds={seconds} limit={limit} />
        )}
      </span>
    </div>
  );
}
